import { useCallback, useEffect, useMemo, useState } from 'react';
// utils
import { useConversationsActsQuery } from '@/services/bff/bff.queries.ts';
import { IChatMessage, IParticipants } from '@/modules/Chat/Chat.types.ts';
import * as types from '@/modules/Chat/Chat.types.ts';
import { ISocketNotification } from '@/modules/Sockets/Sockets.types.ts';
import socket from '@/modules/Sockets/Sockets.utils.ts';

export const useChatHistory = (participants: IParticipants) => {
    const params = useMemo(
        () => ({
            user_persona_id: participants.userPersonaId,
            user_actor_id: participants.userActorId,
            agent_persona_id: participants.agentPersonaId
        }),
        [
            participants.agentPersonaId,
            participants.userActorId,
            participants.userPersonaId
        ]
    );
    const { data, refetch, isFetching } = useConversationsActsQuery({
        ...params,
        per: 100,
        page: 1
    });

    const acts = useMemo(() => (data?.items ?? []).toReversed(), [data?.items]);

    return {
        acts,
        refetch,
        isFetching
    };
};

export const useMessages = ({
    participants,
    onComplete
}: {
    participants: IParticipants;
    onComplete: () => void;
}) => {
    const [messages, setMessages] = useState<types.IChatMessage[]>([]);

    const { agentPersonaId, agentActorId, userPersonaId, userActorId } =
        participants;

    useEffect(() => {
        setMessages([]);
    }, [agentPersonaId, agentActorId, userPersonaId, userActorId]);

    const onMessage = useCallback(
        (event: ISocketNotification) => {
            if (event.event_type !== 'message') return;
            const { data } = event;
            if (
                userPersonaId !== data.user_persona_id ||
                userActorId !== data.user_actor_id ||
                agentPersonaId !== data.agent_persona_id
            )
                return;
            if (data.is_complete) {
                onComplete();
                return;
            }

            const message = messages.find(
                (m) => m.stream_id === event.data.stream_id
            );
            if (message) {
                const updatedMessages = messages.map((m) => {
                    if (m.stream_id === message.stream_id) {
                        return {
                            ...m,
                            content: m.content.concat(event.data.text),
                            datetime: event.data.datetime
                        } as IChatMessage;
                    }
                    return m;
                });
                setMessages(updatedMessages);
                return;
            }
            setMessages([
                ...messages,
                {
                    is_agent: true,
                    stream_id: event.data.stream_id,
                    content: [event.data.text],
                    datetime: event.data.datetime
                }
            ]);
        },
        [agentPersonaId, messages, onComplete, userActorId, userPersonaId]
    );

    useEffect(() => {
        socket.on('notification', onMessage);

        return () => {
            socket.off('notification', onMessage);
        };
    }, [onMessage]);

    return { messages, setMessages };
};
